// src/utils/tripLength.ts

const NUMBER_WORDS: { [word: string]: number } = {
  one: 1,
  two: 2,
  three: 3,
  four: 4,
  five: 5,
  six: 6,
  seven: 7,
  eight: 8,
  nine: 9,
  ten: 10,
  eleven: 11,
  twelve: 12,
  thirteen: 13,
  fourteen: 14,
  fifteen: 15,
  sixteen: 16,
  seventeen: 17,
  eighteen: 18,
  nineteen: 19,
  twenty: 20,
  thirty: 30,
};

// "2 weeks", "two-week", "10 days", "5 nights"
const DURATION_PATTERN = new RegExp(
  `\\b(\\d+|${Object.keys(NUMBER_WORDS).join("|")})[\\s-]+(week|day|night)s?\\b`,
  "gi"
);

// "a week" / "a week-long trip". "a day" and "a night" are deliberately not matched: they usually mean
// "per day" ("two museums a day"), not the length of the trip.
const SINGLE_WEEK_PATTERN = /\ban?\s+week\b/gi;

// "Day 1 - Fly to San Jose", "Day 10: fly home"
const DAY_LABEL_PATTERN = /\bday\s*(\d+)\b/gi;

const toNumber = (value: string) => NUMBER_WORDS[value.toLowerCase()] ?? parseInt(value, 10);

/**
 * Works out how many days the user asked for, counting the departure day, from free text such as the trip name
 * and description. The result is the total number of entries the itinerary's "days" lists should have.
 *
 * Returns undefined unless the text points to exactly one length. Text like "2 days in Rome and 3 days in Paris"
 * is left for the model to interpret: a wrong hard requirement is worse than none.
 */
export const parseTripLengthInDays = (text: string): number | undefined => {
  const lengths = new Set<number>();

  Array.from(text.matchAll(DURATION_PATTERN)).forEach((match) => {
    const count = toNumber(match[1]);
    const unit = match[2].toLowerCase();

    if (unit === "week") {
      lengths.add(count * 7);
    } else if (unit === "night") {
      // N nights plus the departure day
      lengths.add(count + 1);
    } else {
      lengths.add(count);
    }
  });

  if (text.match(SINGLE_WEEK_PATTERN)) {
    lengths.add(7);
  }

  // A day-by-day outline is as long as its highest numbered day
  const dayLabels = Array.from(text.matchAll(DAY_LABEL_PATTERN)).map((match) => parseInt(match[1], 10));
  if (dayLabels.length > 1) {
    lengths.add(Math.max(...dayLabels));
  }

  const [length] = Array.from(lengths);

  return lengths.size === 1 && length > 0 ? length : undefined;
};
